import { useMemo, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PhoneLink } from '@/components/PhoneLink'

type Dealer = {
  id: string
  name: string
  brand: string
  city: string
  province: string
  parts_phone: string | null
  hours_parts: string | null
  refused_ai: boolean
  notes: string | null
  calls_to_date: number
  successful_calls: number
}

function successRate(dealer: Dealer) {
  if (dealer.calls_to_date <= 0) return null
  return Math.round((dealer.successful_calls / dealer.calls_to_date) * 100)
}

export function Dealers() {
  const { t } = useTranslation('dealers')
  const { t: tCommon } = useTranslation('common')
  const { getToken, isLoaded } = useAuth()
  const [search, setSearch] = useState('')

  const dealersQuery = useQuery({
    queryKey: ['dealers'],
    enabled: isLoaded,
    queryFn: async () => {
      const token = await getToken()
      if (!token) throw new Error(tCommon('error.missing_token'))

      const res = await fetch('/api/dealers', {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(
          text || tCommon('error.request_failed', { status: res.status }),
        )
      }

      return (await res.json()) as Dealer[]
    },
  })

  const filtered = useMemo(() => {
    const data = dealersQuery.data ?? []
    const q = search.trim().toLowerCase()
    if (!q) return data
    return data.filter((d) => {
      return (
        d.name.toLowerCase().includes(q) ||
        d.brand.toLowerCase().includes(q) ||
        d.city.toLowerCase().includes(q)
      )
    })
  }, [dealersQuery.data, search])

  const refusingCount = (dealersQuery.data ?? []).filter((d) => d.refused_ai).length

  return (
    <div className="space-y-4">
      {/* §4.5 page header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-bold tracking-[-0.015em] text-foreground [text-wrap:balance]">
            {t('header.title')}
          </h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            {t('header.subtitle', {
              count: dealersQuery.data?.length ?? 0,
              refusing: refusingCount,
            })}
          </p>
        </div>

        {/* §4.10 search input: 48px, 14px radius, leading ink-3 icon */}
        <div className="relative w-full sm:w-72">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-3"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            type="search"
            placeholder={t('search.placeholder')}
            className="h-12 rounded-[14px] pl-9"
          />
        </div>
      </div>

      {dealersQuery.isError ? (
        <div className="rounded-[10px] border border-destructive/30 bg-destructive-soft p-4 text-sm text-destructive">
          {dealersQuery.error instanceof Error
            ? dealersQuery.error.message
            : t('error')}
        </div>
      ) : null}

      <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
        {filtered.map((d) => {
          const pct = successRate(d)
          return (
            <Card key={d.id} className="rounded-[14px] ring-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[15px] font-[620] text-foreground">{d.name}</span>
                    {d.refused_ai ? (
                      // §4.9 neutral chip — refusing AI calls is a fact, not an alarm.
                      <span className="rounded-full border border-border bg-muted px-[9px] py-[3px] text-[12px] font-[650] text-muted-foreground">
                        {t('card.refused_ai')}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {d.brand} · {d.city}, {d.province}
                  </div>
                </div>

                <div className="text-right text-xs">
                  <div className="text-ink-3">{t('card.success_rate')}</div>
                  <div className="font-mono font-semibold text-foreground">
                    {pct === null ? '—' : `${pct}%`}
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[12px] font-medium text-ink-3">
                    {t('card.parts_line')}
                  </div>
                  <div className="font-mono text-foreground">
                    <PhoneLink phone={d.parts_phone} />
                  </div>
                </div>
                <div>
                  <div className="text-[12px] font-medium text-ink-3">
                    {t('card.hours')}
                  </div>
                  <div className="text-muted-foreground">{d.hours_parts || '—'}</div>
                </div>
                <div>
                  <div className="text-[12px] font-medium text-ink-3">
                    {t('card.calls')}
                  </div>
                  <div className="text-muted-foreground">
                    {d.successful_calls} / {d.calls_to_date}
                  </div>
                </div>
                <div />
              </div>

              {d.notes ? (
                <div className="mt-3 rounded-[10px] border border-border bg-muted/40 p-2 text-xs italic text-muted-foreground">
                  {d.notes}
                </div>
              ) : null}
            </Card>
          )
        })}

        {filtered.length === 0 && !dealersQuery.isLoading ? (
          <div className="col-span-full rounded-[14px] border border-dashed border-border bg-muted/30 p-12 text-center text-sm text-muted-foreground">
            {t('empty')}
          </div>
        ) : null}
      </div>
    </div>
  )
}
